import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { apiPost } from '../lib/api';
import Reveal from '../components/Reveal';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', topic: 'general', message: '' });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setErr(null);
    try {
      const res = await apiPost('/contact', form);
      setDone(res.message);
      setForm({ name: '', email: '', topic: 'general', message: '' });
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setSending(false);
    }
  };

  const input =
    'w-full bg-white border border-emerald-950/15 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-flame transition';

  return (
    <div className="bg-cream min-h-screen">
      <div className="bg-forest noise relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <p className="font-mono text-[11px] uppercase tracking-widest2 text-flame mb-3">Contact us</p>
          <h1 className="font-display font-extrabold uppercase tracking-tight text-cream text-4xl sm:text-5xl leading-none">
            Talk to a<br />parts specialist.
          </h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-[1fr_380px] gap-10 items-start">
        <Reveal>
          <div className="bg-white border border-emerald-950/10 rounded-3xl p-8">
            <h2 className="font-display font-bold uppercase text-forest text-2xl mb-1">Send us a message</h2>
            <p className="text-sm text-sub mb-7">Parts questions, bulk orders, or help identifying a component — we answer within one business day.</p>
            {done ? (
              <div data-testid="contact-success-message" className="bg-ok/10 border border-ok/30 text-forest rounded-2xl p-6 flex items-start gap-3">
                <Send className="w-5 h-5 text-ok mt-0.5" />
                <div>
                  <p className="font-semibold">{done}</p>
                  <button onClick={() => setDone(null)} className="text-sm text-sub underline mt-1">Send another message</button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-widest text-sub block mb-1.5">Full name</label>
                  <input data-testid="contact-name-input" required value={form.name} onChange={set('name')} className={input} placeholder="Sam Whitfield" />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-widest text-sub block mb-1.5">Email</label>
                  <input data-testid="contact-email-input" required type="email" value={form.email} onChange={set('email')} className={input} placeholder="sam@farmmail.com" />
                </div>
                <div className="sm:col-span-2">
                  <label className="font-mono text-[10px] uppercase tracking-widest text-sub block mb-1.5">Topic</label>
                  <select data-testid="contact-topic-select" value={form.topic} onChange={set('topic')} className={input}>
                    <option value="general">General question</option>
                    <option value="fitment">Part fitment help</option>
                    <option value="bulk">Bulk / repair shop order</option>
                    <option value="order">Order support</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="font-mono text-[10px] uppercase tracking-widest text-sub block mb-1.5">Message</label>
                  <textarea data-testid="contact-message-input" required rows={5} value={form.message} onChange={set('message')} className={input} placeholder="Tractor model, part numbers, what you need…" />
                </div>
                {err && <p data-testid="contact-error-message" className="sm:col-span-2 text-sm text-bad">{err}</p>}
                <div className="sm:col-span-2">
                  <button data-testid="contact-submit-button" disabled={sending} className="inline-flex items-center gap-2 bg-flame hover:bg-ember disabled:opacity-50 text-white font-display font-semibold uppercase tracking-wide px-8 py-3.5 rounded-full transition-colors">
                    {sending ? 'Sending…' : 'Send Message'} <Send className="w-4 h-4" />
                  </button>
                </div>
              </form>
            )}
          </div>
        </Reveal>

        <Reveal delay={0.12} className="space-y-5">
          <div className="bg-forest text-cream rounded-3xl p-7 noise relative overflow-hidden">
            <h3 className="font-display font-bold uppercase text-2xl">24/7 Parts Desk</h3>
            <div className="mt-5 space-y-3.5 text-sm">
              <p className="flex items-center gap-3"><Phone className="w-4 h-4 text-flame shrink-0" /> 1-800-TRACTOR — toll free, any hour</p>
              <p className="flex items-center gap-3"><Mail className="w-4 h-4 text-flame shrink-0" /> parts@tractorpartsusa.com</p>
              <p className="flex items-center gap-3"><MapPin className="w-4 h-4 text-flame shrink-0" /> Des Moines, Iowa — United States</p>
              <p className="flex items-center gap-3"><Clock className="w-4 h-4 text-flame shrink-0" /> Online orders ship Mon–Sat</p>
            </div>
          </div>
          <div className="bg-white border border-emerald-950/10 rounded-3xl p-7">
            <h3 className="font-display font-bold uppercase text-forest text-xl">Bulk & repair shops</h3>
            <p className="text-sm text-sub mt-2 leading-relaxed">
              Running a shop or managing a fleet? Choose "Bulk / repair shop order" in the form and our
              commercial desk will follow up with fleet pricing and scheduled freight.
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
